import { v4 as uuidv4 } from "uuid";
import { API_NUMBER_DATA_TYPES } from "../constants/api-number-data-type.constant";
import { FIELD_TYPES } from "../constants/dynamicForm.constant";
import { PALETTE_URL_PATTERN } from "../constants/palette-patterns.constant";
import type { FormField } from "../interfaces/dynamicForm.interface";

type FieldType = FormField["type"];

export interface CreateFormFieldOptions {
	id?: string;
	name?: string;
	label?: string;
}

/**
 * Creates a default {@link FormField} for the given type. Pass `id` / `name` / `label`
 * when embedding in flows that already have stable identifiers (e.g. form builder).
 */
export function createFormField(
	type: FieldType,
	options?: CreateFormFieldOptions,
): FormField {
	const id = options?.id ?? uuidv4();
	const name =
		options?.name ?? `${type}_${Math.random().toString(36).substring(7)}`;
	const label =
		options?.label ?? `New ${type.charAt(0).toUpperCase() + type.slice(1)}`;

	const baseField = {
		id,
		name,
		label,
		required: false,
		...(type === FIELD_TYPES.TELEPHONE && { placeholder: "(___) ___-____" }),
	};

	switch (type) {
		case FIELD_TYPES.TEXT:
			return { ...baseField, type: "text" };
		case FIELD_TYPES.NUMBER:
			return {
				...baseField,
				type: "number",
				apiNumberDataType: API_NUMBER_DATA_TYPES.Decimal,
				validation: { min: 0, max: 100 },
			};
		case FIELD_TYPES.TELEPHONE:
			return { ...baseField, type: "phone" };
		case FIELD_TYPES.URL:
			return {
				...baseField,
				type: "url",
				placeholder: "https://",
				validation: {
					pattern: PALETTE_URL_PATTERN,
					custom: "Please enter a valid URL",
				},
			};
		case FIELD_TYPES.DATE_PICKER:
			return { ...baseField, type: "date", placeholder: "Select date" };
		case FIELD_TYPES.TIME_PICKER:
			return { ...baseField, type: "time", placeholder: "Select time" };
		case FIELD_TYPES.DATETIME_PICKER:
			return {
				...baseField,
				type: "dateTime",
				placeholder: "Select date and time",
			};
		case FIELD_TYPES.CHECKBOX:
			return { ...baseField, type: "checkbox", defaultChecked: false };
		case FIELD_TYPES.RADIO:
			return {
				...baseField,
				type: "radio",
				options: [{ value: "option1", label: "Option 1" }],
				defaultValue: "option1",
			};
		case FIELD_TYPES.SELECT:
			return {
				...baseField,
				type: "select",
				options: [{ value: "option1", label: "Option 1" }],
				defaultValue: "option1",
				multiple: false,
			};
		case FIELD_TYPES.SLIDER:
			return {
				...baseField,
				type: "slider",
				min: 0,
				max: 100,
				step: 1,
				defaultValue: 50,
			};
		case FIELD_TYPES.SWITCH:
			return { ...baseField, type: "switch", defaultChecked: false };
		case FIELD_TYPES.TOGGLE_BUTTON:
			return {
				...baseField,
				type: "toggleButton",
				options: [{ value: "option1", label: "Option 1" }],
				defaultValue: "option1",
				exclusive: true,
			};
		case FIELD_TYPES.AUTOCOMPLETE:
			return {
				...baseField,
				type: "autocomplete",
				options: [{ value: "option1", label: "Option 1" }],
				defaultValue: "",
				multiple: false,
				freeSolo: false,
			};
		case FIELD_TYPES.GROUP:
			return {
				...baseField,
				type: "group",
				fields: [],
				accordion: {
					enabled: false,
					defaultExpanded: true,
				},
			};
		case FIELD_TYPES.FIELD_ARRAY:
			return {
				...baseField,
				type: "fieldArray",
				fields: [],
				minItems: 0,
				maxItems: 10,
				addButtonText: "Add Item",
				removeButtonText: "Remove",
				itemLabel: "Item",
			};
		case FIELD_TYPES.FILE_UPLOAD:
			return {
				...baseField,
				type: "file",
				multiple: true,
			};
		case FIELD_TYPES.MULTI_SELECT:
			return {
				...baseField,
				type: "multiSelect",
				options: [{ value: "option1", label: "Option 1" }],
			};
		case FIELD_TYPES.USERS:
			return { ...baseField, type: "users" };
		case FIELD_TYPES.SIGNATURE:
			return { ...baseField, type: "signature" };
		case FIELD_TYPES.ADDRESS:
			return { ...baseField, type: "address" };
		default:
			throw new Error(`Unknown field type: ${type}`);
	}
}
